/**
 * Shapes mirrored from backend `admin-commerce-orders` module. This replaced
 * the old single-vendor `admin-pharmacy-orders` module — orders can now span
 * multiple vendors (one shipment per vendor) across the FOOD and PHARMACY
 * verticals.
 */
export type CommerceVertical = 'FOOD' | 'PHARMACY';

export const COMMERCE_VERTICAL_OPTIONS: CommerceVertical[] = ['FOOD', 'PHARMACY'];

export type CommerceOrderStatus =
  | 'PENDING_PAYMENT'
  | 'PLACED'
  | 'ACCEPTED'
  | 'OUT_FOR_DELIVERY'
  | 'READY'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'REJECTED';

export const COMMERCE_ORDER_STATUS_OPTIONS: CommerceOrderStatus[] = [
  'PENDING_PAYMENT',
  'PLACED',
  'ACCEPTED',
  'OUT_FOR_DELIVERY',
  'READY',
  'COMPLETED',
  'CANCELLED',
  'REJECTED',
];

/** Server-side sort keys. Anything not here is not offered in the UI. */
export const COMMERCE_SORT_FIELDS = [
  'created_at',
  'updated_at',
  'grand_total_inr',
  'status',
] as const;

/** How a status reads to a person, rather than as an enum. */
export const COMMERCE_STATUS_LABELS: Record<CommerceOrderStatus, string> = {
  PENDING_PAYMENT: 'Not paid',
  PLACED: 'Awaiting vendor',
  ACCEPTED: 'Packing',
  OUT_FOR_DELIVERY: 'On its way',
  READY: 'Ready to collect',
  COMPLETED: 'Delivered',
  CANCELLED: 'Cancelled',
  REJECTED: 'Rejected',
};

/** Statuses in which a shipment is still somebody's job. */
export const OPEN_COMMERCE_STATUSES: CommerceOrderStatus[] = [
  'PLACED',
  'ACCEPTED',
  'OUT_FOR_DELIVERY',
  'READY',
];

/** Tiles on the list, each of which is also a filter it can apply. */
export type CommerceCounts = {
  total: number;
  awaiting_vendor: number;
  in_flight: number;
  completed: number;
  cancelled: number;
  rejected: number;
  /** Checkouts abandoned before payment. Out of the default list. */
  unpaid: number;
  /** Shipments moving with no courier reference on them. */
  missing_tracking: number;
  prescription_open: number;
  captured_inr: string;
  refunded_inr: string;
};

export type CommercePrescription = {
  id: string;
  thumb_path: string;
  full_path: string;
};

export type CommerceShipmentLine = {
  id: string;
  name: string;
  variant_label: string | null;
  quantity: number;
  line_total_inr: string;
};

export type CommerceShipmentAudit = {
  id: string;
  from_status: CommerceOrderStatus | null;
  to_status: CommerceOrderStatus;
  actor_type: string;
  actor_id: string | null;
  reason: string | null;
  created_at: string;
};

export type CommerceShipment = {
  id: string;
  vendor_profile_id: string;
  vendor_business_name: string | null;
  status: CommerceOrderStatus;
  subtotal_inr: string;
  tracking_ref: string | null;
  tracking_url: string | null;
  lines: CommerceShipmentLine[];
  /** Only populated on the order-detail response. */
  audits?: CommerceShipmentAudit[];
};

export type CommercePayment = {
  status: string;
  amount_inr: string;
  is_dummy: boolean;
  order_ref: string;
};

export type CommerceOrder = {
  id: string;
  user_id: string;
  vertical: CommerceVertical | null; // null = a mixed FOOD+PHARMACY order
  status: CommerceOrderStatus;
  rollup_status: string;
  goods_total_inr: string;
  delivery_fee_inr: string;
  grand_total_inr: string;
  contact_phone: string;
  address: string | null;
  landmark: string | null;
  city: string | null;
  pincode: string | null;
  notes: string | null;
  prescription_required: boolean;
  prescriptions: CommercePrescription[];
  payment: CommercePayment | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  created_at: string;
  updated_at: string;
  customer_name: string | null;
  /** OTP-only accounts have no name, so this is usually the only identifier. */
  customer_phone: string | null;
  shipments: CommerceShipment[];
};

export type CommerceOrderListResponse = {
  items: CommerceOrder[];
  total: number;
  page: number;
  page_size: number;
};

export type CommerceOrderListFilters = {
  q?: string;
  /** Several statuses at once, comma-separated. Beats `status` when both are set. */
  statuses?: string;
  /** Orders holding at least one shipment in this state — the work is per shipment. */
  shipment_status?: string;
  /** '' = either, 'true' / 'false' = that side only. Matches the URL tri-state. */
  prescription_required?: string;
  missing_tracking?: string;
  from_cart?: string;
  page?: number;
  page_size?: number;
  status?: CommerceOrderStatus;
  vertical?: CommerceVertical;
  /** Inclusive created-date range, YYYY-MM-DD. */
  from?: string;
  to?: string;
  /** Narrow to one vendor. */
  vendor_profile_id?: string;
  /** Narrow to one customer — linked from the customer detail page. */
  user_id?: string;
};
